import { useId, type ReactNode } from "react";
import { api } from "../../../data";
import { Dialog } from "../../../components/ui";
import { currentLang, t } from "../../../lib/sprache";

/**
 * Lokale Formularbausteine der Gruppe „Kalender und Kennzahlen“ (Markup des Pakets: Beschriftung sichtbar über dem Feld,
 * Hinweis und Fehler per aria-describedby). Gemeinsame Bausteine dafür gibt es in src/components noch nicht.
 */

export const rechteText = () => t("Diese Aktion ist für dein Konto nicht freigegeben. Wende dich an deinen Admin.", "This action is not permitted for your account. Contact your admin.");

export function DemoHinweis({ lang = currentLang() }: { lang?: "de" | "en" }) {
  return api.mode === "demo" ? <p className="demo-note">{lang === "en" ? "Demo · All data is fictitious." : "Demo · Alle Daten sind erfunden."}</p> : null;
}

function useBeschreibung(fehler?: string, hinweis?: ReactNode) {
  const id = useId();
  const hinweisId = hinweis ? `${id}-hinweis` : undefined;
  const fehlerId = fehler ? `${id}-fehler` : undefined;
  // K-559 d: eigener Name des Felds (nur die Beschriftung, ohne Hinweis und Fehler), sonst lasen Hilfsmittel „textbox“ ohne Namen
  const labelId = `${id}-label`;
  return { hinweisId, fehlerId, labelId, describedBy: [hinweisId, fehlerId].filter(Boolean).join(" ") || undefined, invalid: fehler ? true : undefined };
}

function Zusatz({ hinweis, hinweisId, fehler, fehlerId }: { hinweis?: ReactNode; hinweisId?: string | undefined; fehler?: string | undefined; fehlerId?: string | undefined }) {
  return (
    <>
      {hinweis && <small id={hinweisId}>{hinweis}</small>}
      {fehler && <small id={fehlerId} className="tk-error">{fehler}</small>}
    </>
  );
}

export function Feld({ label, fehler, hinweis, value, onChange, type = "text", ...rest }: {
  label: ReactNode;
  fehler?: string | undefined;
  hinweis?: ReactNode;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  min?: string;
  max?: string;
  maxLength?: number;
  inputMode?: "text" | "numeric" | "decimal";
  autoFocus?: boolean;
}) {
  const d = useBeschreibung(fehler, hinweis);
  return (
    <label>
      <span id={d.labelId}>{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} aria-labelledby={d.labelId} aria-invalid={d.invalid} aria-describedby={d.describedBy} {...rest} />
      <Zusatz hinweis={hinweis} hinweisId={d.hinweisId} fehler={fehler} fehlerId={d.fehlerId} />
    </label>
  );
}

export function Textfeld({ label, fehler, hinweis, value, onChange, rows = 4, maxLength }: {
  label: ReactNode;
  fehler?: string | undefined;
  hinweis?: ReactNode;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  maxLength?: number;
}) {
  const d = useBeschreibung(fehler, hinweis);
  return (
    <label>
      <span id={d.labelId}>{label}</span>
      <textarea rows={rows} value={value} maxLength={maxLength} onChange={(e) => onChange(e.target.value)} aria-labelledby={d.labelId} aria-invalid={d.invalid} aria-describedby={d.describedBy} />
      <Zusatz hinweis={hinweis} hinweisId={d.hinweisId} fehler={fehler} fehlerId={d.fehlerId} />
    </label>
  );
}

export function Auswahl({ label, fehler, hinweis, value, onChange, optionen, leer, disabled }: {
  label: ReactNode;
  fehler?: string | undefined;
  hinweis?: ReactNode;
  value: string;
  onChange: (v: string) => void;
  optionen: readonly { key: string; label: string }[];
  /** leere erste Auswahl */
  leer?: string;
  disabled?: boolean;
}) {
  const d = useBeschreibung(fehler, hinweis);
  return (
    <label>
      <span id={d.labelId}>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} aria-labelledby={d.labelId} aria-invalid={d.invalid} aria-describedby={d.describedBy} disabled={disabled}>
        {leer !== undefined && <option value="">{leer}</option>}
        {optionen.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
      </select>
      <Zusatz hinweis={hinweis} hinweisId={d.hinweisId} fehler={fehler} fehlerId={d.fehlerId} />
    </label>
  );
}

export function Haken({ label, checked, onChange, disabled, hinweis }: { label: ReactNode; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; hinweis?: ReactNode }) {
  const d = useBeschreibung(undefined, hinweis);
  return (
    <>
      <label className="row">
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} disabled={disabled} aria-describedby={d.describedBy} />
        {label}
      </label>
      {hinweis && <small id={d.hinweisId}>{hinweis}</small>}
    </>
  );
}

/** Zusammenfassung über den Knöpfen: was fehlt oder nicht passt, sonst der Fehler aus der Datenbank. */
export function FormFehler({ fehler, error, lang }: { fehler: Record<string, string>; error?: string | null; lang?: "de" | "en" }) {
  const list = Object.values(fehler);
  if (!list.length && !error) return null;
  const pruefen = lang === "en" ? `Please check ${list.length} entries:` : `Bitte ${list.length} Angaben prüfen:`;
  return (
    <div className="tk-error" role="alert">
      {list.length ? (list.length === 1 ? list[0] : `${pruefen} ${list.slice(0, 3).join(" ")}${list.length > 3 ? " …" : ""}`) : error}
    </div>
  );
}

export function Bestaetigen({ open, titel, text, knopf, gefahr, busy, onJa, onClose, lang }: {
  open: boolean;
  titel: string;
  text: ReactNode;
  knopf: string;
  gefahr?: boolean;
  busy?: boolean;
  onJa: () => void;
  onClose: () => void;
  lang?: "de" | "en";
}) {
  const en = lang === "en";
  return (
    <Dialog open={open} onClose={onClose} title={titel}>
      <p>{text}</p>
      <div className="row">
        <button type="button" className={gefahr ? "danger" : "primary"} onClick={onJa} disabled={busy} aria-busy={busy}>{busy ? (en ? "Working …" : "Wird ausgeführt …") : knopf}</button>
        <button type="button" onClick={onClose}>{en ? "Cancel" : "Abbrechen"}</button>
      </div>
    </Dialog>
  );
}

/** Datei im Browser erzeugen und herunterladen (Export). Nichts verlässt den Browser. */
export function ladeHerunter(name: string, inhalt: string, typ = "text/csv;charset=utf-8"): void {
  const url = URL.createObjectURL(new Blob([inhalt], { type: typ }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Wert aus dem Browser-Speicher (nur Komfort wie „zuletzt gesehen“); ohne Speicher null. */
export function gespeichert(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
export function speichere(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ohne Speicher: gilt bis zum Neuladen */
  }
}
